import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useStoreConfig } from '@/contexts/StoreConfigContext';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, Package, Layers, ShoppingCart, Ticket, Home, Mail, Sparkles, Rss, Settings, LogOut, Menu, X, ChevronRight, Zap, Upload, Bell, Moon, Sun } from 'lucide-react';

const adminLinks = [
  { to: '/admin/dashboard', icon: LayoutDashboard, key: 'dashboard', section: 'main' },
  { to: '/admin/products', icon: Package, key: 'products', section: 'catalog', adminOnly: true },
  { to: '/admin/bundles', icon: Layers, key: 'bundles', section: 'catalog', adminOnly: true },
  { to: '/admin/import', icon: Upload, key: 'import', section: 'catalog', adminOnly: true },
  { to: '/admin/orders', icon: ShoppingCart, key: 'orders', section: 'sales' },
  { to: '/admin/coupons', icon: Ticket, key: 'coupons', section: 'sales', adminOnly: true },
  { to: '/admin/upsells', icon: Zap, key: 'upsells', section: 'sales', adminOnly: true },
  { to: '/admin/notifications', icon: Bell, key: 'notifications', section: 'marketing', adminOnly: true },
  { to: '/admin/homepage', icon: Home, key: 'homepage', section: 'content', adminOnly: true },
  { to: '/admin/emails', icon: Mail, key: 'emails', section: 'content', adminOnly: true },
  { to: '/admin/ai', icon: Sparkles, key: 'ai', section: 'tools', adminOnly: true },
  { to: '/admin/feed', icon: Rss, key: 'feed', section: 'tools', adminOnly: true },
  { to: '/admin/settings', icon: Settings, key: 'settings', section: 'system', adminOnly: true },
];

const sections = { main: '', catalog: 'Catalog', sales: 'Sales', marketing: 'Marketing', content: 'Content', tools: 'Tools', system: 'System' };

export default function Sidebar({ user, dark, onToggleDark }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { config } = useStoreConfig();
  const { t } = useTranslation();
  const storeName = config?.store_name || 'Store';
  const [mobileOpen, setMobileOpen] = useState(false);

  const links = user?.role === 'supplier'
    ? adminLinks.filter(l => !l.adminOnly)
    : adminLinks;

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const NavContent = () => {
    let lastSection = '';
    return (
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="px-5 py-5 border-b" style={{ borderColor: dark ? '#333' : 'rgba(26,26,26,0.06)' }}>
          <Link to="/" className="font-serif text-lg tracking-tight" style={{ color: dark ? '#fff' : '#1A1A1A' }}>
            Florashop
          </Link>
          <p className="font-sans text-[10px] uppercase tracking-wider mt-0.5" style={{ color: '#C4956A' }}>Admin</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {links.map(link => {
            const Icon = link.icon;
            const isActive = location.pathname === link.to || location.pathname.startsWith(link.to + '/') || (link.to === '/admin/dashboard' && location.pathname === '/admin');
            const showSection = link.section !== lastSection && sections[link.section];
            lastSection = link.section;
            return (
              <div key={link.key}>
                {showSection && (
                  <p className="font-sans text-[10px] uppercase tracking-wider px-3 mt-5 mb-2" style={{ color: dark ? '#666' : 'rgba(26,26,26,0.35)' }}>
                    {showSection}
                  </p>
                )}
                <Link to={link.to} onClick={() => setMobileOpen(false)}
                  data-testid={`sidebar-${link.key}`}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg mb-0.5 font-sans text-sm transition-all duration-200 group"
                  style={{
                    backgroundColor: isActive ? (dark ? 'rgba(255,255,255,0.08)' : 'rgba(26,26,26,0.05)') : 'transparent',
                    color: isActive ? (dark ? '#fff' : '#1A1A1A') : (dark ? '#aaa' : 'rgba(26,26,26,0.55)'),
                    fontWeight: isActive ? 500 : 400,
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(26,26,26,0.03)'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                  <Icon size={16} strokeWidth={1.5} />
                  {t(`admin.sidebar.${link.key}`)}
                  {isActive && <ChevronRight size={14} strokeWidth={1.5} className="ml-auto opacity-40" />}
                </Link>
              </div>
            );
          })}
        </nav>

        {/* User + Dark Mode + Logout */}
        <div className="px-3 py-4 border-t" style={{ borderColor: dark ? '#333' : 'rgba(26,26,26,0.06)' }}>
          {/* Dark mode toggle */}
          <button data-testid="dark-mode-toggle" onClick={onToggleDark}
            className="flex items-center gap-3 px-3 py-2 rounded-lg w-full font-sans text-xs mb-2 transition-colors"
            style={{ color: dark ? '#aaa' : 'rgba(26,26,26,0.5)' }}>
            {dark ? <Sun size={15} /> : <Moon size={15} />}
            {dark ? 'Light Mode' : 'Dark Mode'}
          </button>
          <div className="flex items-center gap-3 px-3 mb-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center font-sans text-xs font-medium" style={{ backgroundColor: '#C4956A', color: '#FFFFFF' }}>
              {(user?.name || 'A')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-sans text-sm truncate" style={{ color: dark ? '#fff' : '#1A1A1A' }}>{user?.name || 'Admin'}</p>
              <p className="font-sans text-[10px] uppercase tracking-wider" style={{ color: dark ? '#666' : 'rgba(26,26,26,0.4)' }}>{user?.role}</p>
            </div>
          </div>
          <button data-testid="sidebar-logout" onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 rounded-lg w-full font-sans text-sm transition-colors duration-200"
            style={{ color: 'rgba(26,26,26,0.5)' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(232,93,58,0.06)'; e.currentTarget.style.color = '#E85D3A'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'rgba(26,26,26,0.5)'; }}>
            <LogOut size={16} strokeWidth={1.5} />
            Logout
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Desktop sidebar */}
      <aside data-testid="admin-sidebar" className="hidden md:flex w-64 flex-shrink-0 border-r flex-col"
        style={{ backgroundColor: dark ? '#1A1A1A' : '#FDFBF7', borderColor: dark ? '#333' : 'rgba(26,26,26,0.06)' }}>
        <NavContent />
      </aside>

      {/* Mobile toggle */}
      <button data-testid="admin-mobile-menu" className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg"
        style={{ backgroundColor: dark ? '#1A1A1A' : '#FDFBF7', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        onClick={() => setMobileOpen(!mobileOpen)}>
        {mobileOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
      </button>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <>
          <div className="md:hidden fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="md:hidden fixed inset-y-0 left-0 z-50 w-64 shadow-xl" style={{ backgroundColor: dark ? '#1A1A1A' : '#FDFBF7' }}>
            <NavContent />
          </aside>
        </>
      )}
    </>
  );
}
